/**
 * Map Instance Manager Utility
 *
 * Centralized management of ArcGIS map instances to ensure all components
 * reference the same active map, preventing layers from being added to stale references.
 *
 * Key Features:
 * - Single source of truth for current map instance
 * - Safe layer operations (add, find, remove)
 * - Backward compatibility with window.loadedMap/loadedView
 * - Debug logging for troubleshooting
 *
 * Usage:
 * - Call registerMapInstance() when map is created
 * - Use addLayerToMap(), findLayerById() instead of direct map operations
 * - Call unregisterMapInstance() on component unmount
 */

import { debugLog, debugWarn, debugError } from './basemap-debug.util';

interface MapInstance {
  map: __esri.WebMap | __esri.Map;
  view: __esri.MapView;
  modules: any;
  timestamp: number;
}

let currentMapInstance: MapInstance | null = null;

/**
 * Registers the current active map instance
 * This should be called by Map/WebMap components when they initialize
 *
 * @param map - The WebMap or Map instance
 * @param view - The MapView instance
 * @param modules - Loaded ArcGIS modules
 */
export function registerMapInstance(
  map: __esri.WebMap | __esri.Map,
  view: __esri.MapView,
  modules: any
): void {
  currentMapInstance = {
    map,
    view,
    modules,
    timestamp: Date.now()
  };

  // Also update window references for backward compatibility
  window.loadedMap = map;
  window.loadedView = view;
  window.loadedMapModules = modules;

  const mapAny: any = map;
  const mapId = mapAny.id || 'unknown';
  debugLog('[Map Instance] Registered new map instance', {
    mapId: mapId,
    timestamp: currentMapInstance.timestamp
  });
}

/**
 * Gets the current active map instance
 * Returns null if no map is registered
 *
 * @returns MapInstance | null
 */
export function getCurrentMapInstance(): MapInstance | null {
  return currentMapInstance;
}

/**
 * Safely adds a layer to the current map instance
 * Ensures the layer is added to the correct map (view.map, not stale reference)
 *
 * @param layer - The layer to add
 * @param index - Optional index position for the layer
 * @returns boolean - True if successful, false otherwise
 */
export function addLayerToMap(layer: __esri.Layer, index?: number): boolean {
  if (!currentMapInstance) {
    debugError('[Map Instance] No active map instance to add layer to');
    return false;
  }

  if (!currentMapInstance.view || !currentMapInstance.view.map) {
    debugError('[Map Instance] Map view or map is not ready yet');
    return false;
  }

  try {
    // Always use the view's map, not the stored map reference
    // This prevents adding layers to stale map instances
    if (typeof index === 'number') {
      currentMapInstance.view.map.add(layer, index);
    } else {
      currentMapInstance.view.map.add(layer);
    }
    debugLog('[Map Instance] Layer added successfully:', layer.id);
    return true;
  } catch (error) {
    debugError('[Map Instance] Failed to add layer:', error);
    return false;
  }
}

/**
 * Safely finds a layer by ID from the current map instance
 *
 * @param layerId - The ID of the layer to find
 * @param silent - If true, don't log warning when layer is not found (default: false)
 * @returns __esri.Layer | null
 */
export function findLayerById(
  layerId: string,
  silent: boolean = false
): __esri.Layer | null {
  if (!currentMapInstance) {
    debugWarn('[Map Instance] No active map instance to find layer from');
    return null;
  }

  if (!currentMapInstance.view || !currentMapInstance.view.map) {
    debugWarn('[Map Instance] Map view or map is not ready yet');
    return null;
  }

  try {
    // Always use the view's map to find layers
    const layer = currentMapInstance.view.map.findLayerById(layerId);
    if (!layer && !silent) {
      debugWarn(`[Map Instance] Layer not found: ${layerId}`);
    }
    return layer;
  } catch (error) {
    debugError('[Map Instance] Error finding layer:', error);
    return null;
  }
}

/**
 * Safely removes a layer from the current map instance
 *
 * @param layerId - The ID of the layer to remove
 * @returns boolean - True if successful, false otherwise
 */
export function removeLayerFromMap(layerId: string): boolean {
  if (!currentMapInstance) {
    debugError('[Map Instance] No active map instance to remove layer from');
    return false;
  }

  if (!currentMapInstance.view || !currentMapInstance.view.map) {
    debugError('[Map Instance] Map view or map is not ready yet');
    return false;
  }

  try {
    const layer = currentMapInstance.view.map.findLayerById(layerId);
    if (layer) {
      currentMapInstance.view.map.remove(layer);
      debugLog('[Map Instance] Layer removed successfully:', layerId);
      return true;
    } else {
      debugWarn(`[Map Instance] Layer not found for removal: ${layerId}`);
      return false;
    }
  } catch (error) {
    debugError('[Map Instance] Failed to remove layer:', error);
    return false;
  }
}

/**
 * Gets all layers from the current map instance
 *
 * @returns __esri.Collection<__esri.Layer> | null
 */
export function getAllLayers(): __esri.Collection<__esri.Layer> | null {
  if (!currentMapInstance) {
    debugWarn('[Map Instance] No active map instance');
    return null;
  }

  if (!currentMapInstance.view || !currentMapInstance.view.map) {
    debugWarn('[Map Instance] Map view or map is not ready yet');
    return null;
  }

  return currentMapInstance.view.map.layers;
}

/**
 * Clears the current map instance reference
 * Should be called on component unmount
 */
export function unregisterMapInstance(): void {
  if (currentMapInstance) {
    debugLog('[Map Instance] Unregistered map instance', {
      timestamp: currentMapInstance.timestamp
    });
  }
  currentMapInstance = null;
}

/**
 * Checks if a map instance is currently registered
 *
 * @returns boolean
 */
export function hasMapInstance(): boolean {
  return currentMapInstance !== null;
}

/**
 * Returns the active map to mutate during basemap switching.
 * Prefers the registered instance, then window refs, skipping destroyed views.
 */
export function getActiveMapForMutation(): __esri.Map | __esri.WebMap | null {
  if (
    currentMapInstance?.view &&
    !currentMapInstance.view.destroyed &&
    currentMapInstance.view.map
  ) {
    return currentMapInstance.view.map;
  }

  const { loadedView, loadedMap } = window;
  if (loadedView && !loadedView.destroyed && loadedView.map) {
    return loadedView.map;
  }

  if (loadedMap) {
    return loadedMap;
  }

  return null;
}
